import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { money } from "@/app/pages/commissions/CommissionList";

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const REWARD_STYLE = {
  pending: "bg-amber-50 text-amber-700", approved: "bg-indigo-50 text-indigo-700",
  payable: "bg-cobalt/10 text-cobalt", paid: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700",
};
const REFERRAL_STYLE = {
  clicked: "bg-slate-100 text-slate-600", attributed: "bg-sky-50 text-sky-700", qualified: "bg-cobalt/10 text-cobalt",
  expired: "bg-slate-100 text-slate-400", blocked: "bg-rose-50 text-rose-700",
};

export default function AffiliateDetail() {
  const { affiliateId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get(`/admin/affiliates/${affiliateId}`).then((r) => setData(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Affiliate not found")), [affiliateId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${affiliateId}/status`, { status });
      toast.success(`Affiliate ${status.replaceAll("_", " ")}.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const rewardAction = async (rewardId, action) => {
    setBusy(true);
    try {
      await api.post(`/admin/affiliate-rewards/${rewardId}/${action}`, action === "reject" ? { reason: "Rejected by admin" } : {});
      toast.success(`Reward ${action}d.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const recordPayout = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${affiliateId}/payouts`, { reward_ids: selected, reference, note: note || undefined });
      toast.success("Manual payout recorded.");
      setSelected([]); setReference(""); setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="rounded-2xl bg-rose-50 p-4 text-sm font-semibold text-rose-700" data-testid="affiliate-error">{error}</p>;
  if (!data) return <p className="text-sm text-slatey">Loading…</p>;

  const { affiliate, referrals, rewards, payouts, term_acceptances } = data;
  const payable = rewards.filter((r) => r.status === "payable");
  const selectedTotal = payable.filter((r) => selected.includes(r.id)).reduce((sum, r) => sum + r.amount, 0);

  return (
    <div data-testid="admin-affiliate-detail" className="space-y-6">
      <Link to="/app/admin/affiliates" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> All affiliates</Link>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink" data-testid="affiliate-name">
              {affiliate.name} {affiliate.is_demo && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">TEST</span>}
            </h1>
            <p className="mt-1 text-sm text-slatey">{affiliate.email} · code <span className="font-mono font-bold text-cobalt">{affiliate.code}</span></p>
            <p className="mt-1 text-xs text-slatey">Terms v{affiliate.terms_version} accepted {affiliate.terms_accepted_at?.slice(0, 10)}</p>
          </div>
          <span className="rounded-full bg-cobalt/10 px-4 py-2 text-sm font-bold capitalize text-cobalt" data-testid="affiliate-detail-status">{affiliate.status.replaceAll("_", " ")}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="affiliate-status-actions">
          {affiliate.status === "pending_approval" && <button onClick={() => setStatus("active")} disabled={busy} data-testid="affiliate-approve" className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">Approve</button>}
          {affiliate.status !== "suspended" && <button onClick={() => setStatus("suspended")} disabled={busy} data-testid="affiliate-suspend" className="rounded-full border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 disabled:opacity-50">Suspend</button>}
          {affiliate.status === "suspended" && <button onClick={() => setStatus("active")} disabled={busy} data-testid="affiliate-reactivate" className="rounded-full bg-cobalt px-4 py-2 text-xs font-bold text-white disabled:opacity-50">Reactivate</button>}
          {affiliate.status !== "inactive" && <button onClick={() => setStatus("inactive")} disabled={busy} data-testid="affiliate-deactivate" className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-ink disabled:opacity-50">Deactivate</button>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="admin-affiliate-referrals">
          <h2 className="font-display text-xl font-bold text-ink">Referral attribution</h2>
          <ul className="mt-3 space-y-2">
            {referrals.map((referral, index) => (
              <li key={referral.id || index} className="rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`referral-${referral.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slatey">{referral.clicked_at?.slice(0, 16).replace("T", " ")} · {referral.landing_path || "/"}</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${REFERRAL_STYLE[referral.status]}`} data-testid={`referral-status-${referral.id}`}>{referral.status}</span>
                </div>
                <p className="mt-1 text-xs text-slatey">
                  {referral.learner_id ? "Registered learner linked" : "Visitor (not registered)"} · {referral.journey_started ? "journey started" : "no journey"} · {(referral.application_ids || []).length} application(s)
                </p>
                {(referral.flags || []).length > 0 && (
                  <p className="mt-1 text-xs font-bold text-rose-600" data-testid={`referral-flags-${referral.id}`}>
                    Flags: {referral.flags.map((f) => f.flag.replaceAll("_", " ")).join(", ")}
                  </p>
                )}
              </li>
            ))}
            {!referrals.length && <li className="text-sm text-slatey">No referral clicks yet.</li>}
          </ul>
        </section>

        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="admin-affiliate-rewards">
            <h2 className="font-display text-xl font-bold text-ink">Rewards</h2>
            <ul className="mt-3 space-y-2">
              {rewards.map((reward, index) => (
                <li key={reward.id || index} className="rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`admin-reward-${reward.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-ink">{money(reward.amount, reward.currency)}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${REWARD_STYLE[reward.status]}`} data-testid={`admin-reward-status-${reward.id}`}>{reward.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-slatey">{reward.qualifying_event.replaceAll("_", " ")} · {reward.created_at?.slice(0, 10)} · config v{reward.config_version}</p>
                  {(reward.flags || []).length > 0 && <p className="mt-1 text-xs font-bold text-amber-700">Flags: {reward.flags.map((f) => f.flag.replaceAll("_", " ")).join(", ")}</p>}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {reward.status === "pending" && (
                      <>
                        <button onClick={() => rewardAction(reward.id, "approve")} disabled={busy} data-testid={`reward-approve-${reward.id}`} className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Approve</button>
                        <button onClick={() => rewardAction(reward.id, "reject")} disabled={busy} data-testid={`reward-reject-${reward.id}`} className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 disabled:opacity-50">Reject</button>
                      </>
                    )}
                    {reward.status === "approved" && (
                      <button onClick={() => rewardAction(reward.id, "payable")} disabled={busy} data-testid={`reward-payable-${reward.id}`} className="rounded-full bg-cobalt px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Mark payable</button>
                    )}
                    {reward.status === "payable" && (
                      <label className="flex items-center gap-2 text-xs font-semibold text-ink">
                        <input type="checkbox" checked={selected.includes(reward.id)}
                          onChange={(e) => setSelected((s) => e.target.checked ? [...s, reward.id] : s.filter((id) => id !== reward.id))}
                          className="h-4 w-4 accent-cobalt" data-testid={`reward-select-${reward.id}`} /> Include in payout
                      </label>
                    )}
                  </div>
                </li>
              ))}
              {!rewards.length && <li className="text-sm text-slatey">No rewards yet.</li>}
            </ul>

            {payable.length > 0 && (
              <div className="mt-4 rounded-2xl border border-slate-200 p-4" data-testid="payout-panel">
                <p className="text-xs font-bold uppercase text-slatey">Record manual payout</p>
                <p className="mt-1 text-sm font-bold text-ink" data-testid="payout-selected-total">Selected: {money(selectedTotal, "GBP")}</p>
                <input value={reference} onChange={(e) => setReference(e.target.value)} className={`${input} mt-2`} placeholder="Payment reference (e.g. bank transfer ref)" data-testid="payout-reference" />
                <input value={note} onChange={(e) => setNote(e.target.value)} className={`${input} mt-2`} placeholder="Note (optional)" data-testid="payout-note" />
                <button onClick={recordPayout} disabled={busy || !selected.length || reference.trim().length < 2} data-testid="payout-record"
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record payment
                </button>
              </div>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="admin-affiliate-payment">
            <h2 className="font-display text-xl font-bold text-ink">Payout details</h2>
            {affiliate.payment_details ? (
              <dl className="mt-2 space-y-1 text-sm" data-testid="admin-payment-full">
                <div className="flex justify-between"><dt className="text-slatey">Account holder</dt><dd className="font-semibold text-ink">{affiliate.payment_details.account_holder}</dd></div>
                <div className="flex justify-between"><dt className="text-slatey">Sort code</dt><dd className="font-semibold text-ink">{affiliate.payment_details.sort_code}</dd></div>
                <div className="flex justify-between"><dt className="text-slatey">Account number</dt><dd className="font-semibold text-ink">{affiliate.payment_details.account_number}</dd></div>
                <div className="flex justify-between"><dt className="text-slatey">Country</dt><dd className="font-semibold text-ink">{affiliate.payment_details.country}</dd></div>
              </dl>
            ) : <p className="mt-2 text-sm text-slatey">No payment details submitted.</p>}
            <h3 className="mt-4 text-xs font-bold uppercase text-slatey">Payment history</h3>
            <ul className="mt-2 space-y-2">
              {payouts.map((payout, index) => (
                <li key={payout.id || index} className="rounded-2xl bg-surface px-4 py-3 text-xs" data-testid={`admin-payout-${payout.id}`}>
                  <span className="font-bold text-ink">{money(payout.amount, payout.currency)}</span> · ref {payout.reference} · {payout.paid_at?.slice(0, 10)} · by {payout.recorded_by}
                </li>
              ))}
              {!payouts.length && <li className="text-sm text-slatey">No payouts recorded.</li>}
            </ul>
            <h3 className="mt-4 text-xs font-bold uppercase text-slatey">Terms acceptance audit</h3>
            <ul className="mt-2 space-y-1">
              {term_acceptances.map((acceptance, index) => (
                <li key={acceptance.id || index} className="text-xs text-slatey" data-testid={`terms-acceptance-${index}`}>
                  v{acceptance.version} · {acceptance.accepted_at?.slice(0, 16).replace("T", " ")} · IP {acceptance.ip || "n/a"}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
